// Enhanced Form Sections with Dynamic Flow Rules for Capitolato
import React from 'react';
import { TextField } from '../../layout';
import {
    NINFA_MODELS,
    CATEGORY_OPTIONS,
    FINISH_OPTIONS,
    GLASS_OPTIONS,
    FIXING_OPTIONS,
    DRAINAGE_HOLES_OPTIONS,
    MILLING_OPTIONS,
    PLATES_OPTIONS,
    MODEL_RENDER_FOLDER
} from '../../utils/capitolatoData';
import { NINFA_FLOW_RULES, getAvailableOptions, filterOptionsByRules } from '../../utils/ninfaFlowRules';

// Reusable Radio Button Component
const RadioOption = ({ id, value, label, description, selected, onChange, showDescription = false }) => (
    <div className="mb-4">
        <label
            htmlFor={id}
            className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${selected === value
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-red-300 bg-white'
                }`}
        >
            <input
                type="radio"
                id={id}
                name={id.split('-')[0]}
                value={value}
                checked={selected === value}
                onChange={() => onChange(value)}
                className="mt-1 mr-3 w-4 h-4 text-red-600 focus:ring-red-500"
            />
            <div className="flex-1">
                <div className="font-medium text-gray-900">{label}</div>
                {showDescription && description && (
                    <p className="mt-1 text-sm text-gray-600">{description}</p>
                )}
            </div>
        </label>
    </div>
);

// Section 1: Header Section (Contact Information) - UNCHANGED
export const HeaderSection = ({ formData, onChange, disabled = false }) => {
    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Informazioni di Contatto</h2>

            {disabled && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-700">
                        I campi di contatto sono bloccati in modalità revisione.
                    </p>
                </div>
            )}

            <TextField
                id="nominativi"
                label="Nominativi"
                placeholder="Es: Spett.le Baroni + Piscone architettura"
                value={formData.nominativi || ''}
                onChange={(e) => onChange('nominativi', e.target.value)}
                required
                fullWidth
                disabled={disabled}
            />

            <TextField
                id="indirizzo"
                label="Indirizzo"
                placeholder="Es: Via San Marco, 46 - Milano"
                value={formData.indirizzo || ''}
                onChange={(e) => onChange('indirizzo', e.target.value)}
                required
                fullWidth
                disabled={disabled}
            />

            <TextField
                id="telefono"
                label="Recapito telefonico"
                type="tel"
                placeholder="Es: +39.02.29.00.35.23"
                value={formData.telefono || ''}
                onChange={(e) => onChange('telefono', e.target.value)}
                required
                fullWidth
                disabled={disabled}
            />

            <TextField
                id="email"
                label="Recapito e-mail"
                type="email"
                placeholder="Es: info@example.com"
                value={formData.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                required
                fullWidth
                disabled={disabled}
            />

            <TextField
                id="rifCantiere"
                label="Rif. Cantiere (Riferimento Cantiere)"
                placeholder="Es: Milano"
                value={formData.rifCantiere || ''}
                onChange={(e) => onChange('rifCantiere', e.target.value)}
                required
                fullWidth
                disabled={disabled}
            />
        </div>
    );
};

// Section 2: Ninfa Model Selection - UNCHANGED
export const NinfaModelSection = ({ formData, onChange }) => {
    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Modello Ninfa</h2>
            <p className="text-gray-600 mb-6">Seleziona il modello di balaustra Ninfa</p>

            <div className="space-y-3">
                {NINFA_MODELS.map((model) => (
                    <RadioOption
                        key={model.value}
                        id={`ninfa-${model.value}`}
                        value={model.value}
                        label={model.label}
                        selected={formData.modelloNinfa}
                        onChange={(value) => onChange('modelloNinfa', value)}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 3: Category Selection - DYNAMIC based on Ninfa Model
export const CategorySection = ({ formData, onChange }) => {
    // Get available categories for the selected Ninfa model
    const selectedModel = formData.modelloNinfa;
    let availableCategories = CATEGORY_OPTIONS;

    if (selectedModel) {
        const modelRules = NINFA_FLOW_RULES[selectedModel];
        if (modelRules) {
            const allowedCategoryValues = modelRules.map(rule => rule.category);
            availableCategories = CATEGORY_OPTIONS.filter(cat =>
                allowedCategoryValues.includes(cat.value)
            );
        }
    }

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Categoria Appartenenza</h2>
            <p className="text-gray-600 mb-6">Seleziona la classe di appartenenza secondo il DM 17/01/2018</p>

            {!selectedModel && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                        Seleziona prima un modello Ninfa per visualizzare le categorie disponibili
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {availableCategories.map((category) => (
                    <RadioOption
                        key={category.value}
                        id={`category-${category.value}`}
                        value={category.value}
                        label={category.label}
                        selected={formData.categoria}
                        onChange={(value) => onChange('categoria', value)}
                    />
                ))}
            </div>

            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                    <strong>Classe C2:</strong> Aree con posti a sedere fissi (chiese, teatri, cinema, sale conferenze, aule) - Carico 2 kN/m
                </p>
                <p className="text-sm text-red-800 mt-2">
                    <strong>Classe C3:</strong> Ambienti privi di ostacoli (musei, sale esposizioni, aree accesso uffici/alberghi/ospedali) - Carico 3 kN/m
                </p>
                <p className="text-sm text-red-800 mt-2">
                    <strong>UNI 13200-3:</strong> Crush Barrier
                </p>
            </div>
        </div>
    );
};

// Section 4: Aluminum Finish Selection - DYNAMIC
export const FinishSection = ({ formData, onChange }) => {
    const selectedFinish = FINISH_OPTIONS.find(f => f.value === formData.finituraAlluminio);
    const requiresCustomCode = selectedFinish?.requiresCode;

    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availableFinishOptions = FINISH_OPTIONS;

    if (rules && rules.finishOptions !== 'ALL') {
        availableFinishOptions = filterOptionsByRules(FINISH_OPTIONS, rules.finishOptions);
    }

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Finiture Alluminio</h2>
            <p className="text-gray-600 mb-6">Seleziona il tipo di finitura per i profili in alluminio</p>

            <div className="space-y-3">
                {availableFinishOptions.map((finish) => (
                    <RadioOption
                        key={finish.value}
                        id={`finish-${finish.value}`}
                        value={finish.value}
                        label={finish.label}
                        selected={formData.finituraAlluminio}
                        onChange={(value) => onChange('finituraAlluminio', value)}
                        showDescription={false}
                    />
                ))}
            </div>

            {requiresCustomCode && (
                <div className="mt-4">
                    <TextField
                        id="ralCustomCode"
                        label="Codice RAL Personalizzato"
                        placeholder="Es: 7016"
                        value={formData.ralCustomCode || ''}
                        onChange={(e) => onChange('ralCustomCode', e.target.value)}
                        required
                        fullWidth
                    />
                </div>
            )}
        </div>
    );
};

// Section 5: Glass Thickness Selection - DYNAMIC
export const GlassSection = ({ formData, onChange }) => {
    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availableGlassOptions = GLASS_OPTIONS;

    if (rules && rules.glassOptions) {
        availableGlassOptions = filterOptionsByRules(GLASS_OPTIONS, rules.glassOptions);
    }

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Spessore Vetro</h2>
            <p className="text-gray-600 mb-6">Seleziona lo spessore e il tipo di stratificazione del vetro</p>

            {availableGlassOptions.length === 0 && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                        Nessuna opzione di vetro disponibile per la configurazione selezionata
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {availableGlassOptions.map((glass) => (
                    <RadioOption
                        key={glass.value}
                        id={`glass-${glass.value}`}
                        value={glass.value}
                        label={glass.label}
                        selected={formData.spessoreVetro}
                        onChange={(value) => onChange('spessoreVetro', value)}
                        showDescription={false}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 6: Fixing Type Selection - DYNAMIC
export const FixingSection = ({ formData, onChange }) => {
    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availableFixingOptions = FIXING_OPTIONS;

    if (rules && rules.fixingOptions !== 'ALL') {
        availableFixingOptions = filterOptionsByRules(FIXING_OPTIONS, rules.fixingOptions);
    }

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Tipo di Fissaggio</h2>
            <p className="text-gray-600 mb-6">Seleziona il tipo di fissaggio del profilo</p>

            <div className="space-y-3">
                {availableFixingOptions.map((fixing) => (
                    <RadioOption
                        key={fixing.value}
                        id={`fixing-${fixing.value}`}
                        value={fixing.value}
                        label={fixing.label}
                        selected={formData.tipoFissaggio}
                        onChange={(value) => onChange('tipoFissaggio', value)}
                        showDescription={false}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 7: Drainage Holes Selection - DYNAMIC
export const DrainageHolesSection = ({ formData, onChange }) => {
    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availableDrainageOptions = DRAINAGE_HOLES_OPTIONS;

    if (rules && rules.drainageHoles) {
        availableDrainageOptions = filterOptionsByRules(DRAINAGE_HOLES_OPTIONS, rules.drainageHoles);
    }

    // Auto-select if only one option
    React.useEffect(() => {
        if (availableDrainageOptions.length === 1 && !formData.foriScarico) {
            onChange('foriScarico', availableDrainageOptions[0].value);
        }
    }, [availableDrainageOptions, formData.foriScarico, onChange]);

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Fori di Scarico</h2>
            <p className="text-gray-600 mb-6">Il profilo prevede fori di scarico acqua?</p>

            {availableDrainageOptions.length === 1 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">
                        Per questo modello e categoria, l'opzione è preimpostata: <strong>{availableDrainageOptions[0].label}</strong>
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {availableDrainageOptions.map((option) => (
                    <RadioOption
                        key={option.value}
                        id={`drainage-${option.value}`}
                        value={option.value}
                        label={option.label}
                        selected={formData.foriScarico}
                        onChange={(value) => onChange('foriScarico', value)}
                        showDescription={false}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 8: Milling Option Selection - DYNAMIC
export const MillingSection = ({ formData, onChange }) => {
    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availableMillingOptions = MILLING_OPTIONS;

    if (rules && rules.milling) {
        availableMillingOptions = filterOptionsByRules(MILLING_OPTIONS, rules.milling);
    }

    // Auto-select if only one option
    React.useEffect(() => {
        if (availableMillingOptions.length === 1 && !formData.fresaturaScarico) {
            onChange('fresaturaScarico', availableMillingOptions[0].value);
        }
    }, [availableMillingOptions, formData.fresaturaScarico, onChange]);

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Optional Fresatura Scarico Acqua</h2>
            <p className="text-gray-600 mb-6">Il profilo prevede fresature di scarico acqua?</p>

            {availableMillingOptions.length === 1 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">
                        Per questo modello e categoria, l'opzione è preimpostata: <strong>{availableMillingOptions[0].label}</strong>
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {availableMillingOptions.map((option) => (
                    <RadioOption
                        key={option.value}
                        id={`milling-${option.value}`}
                        value={option.value}
                        label={option.label}
                        selected={formData.fresaturaScarico}
                        onChange={(value) => onChange('fresaturaScarico', value)}
                        showDescription={false}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 9: Plates Option Selection - DYNAMIC
export const PlatesSection = ({ formData, onChange }) => {
    // Get flow rules
    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
    let availablePlatesOptions = PLATES_OPTIONS;

    if (rules && rules.plates) {
        availablePlatesOptions = filterOptionsByRules(PLATES_OPTIONS, rules.plates);
    }

    // Auto-select if only one option
    React.useEffect(() => {
        if (availablePlatesOptions.length === 1 && !formData.piattiScarico) {
            onChange('piattiScarico', availablePlatesOptions[0].value);
        }
    }, [availablePlatesOptions, formData.piattiScarico, onChange]);

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Optional Piatti Scarico Acqua</h2>
            <p className="text-gray-600 mb-6">Il profilo viene montato su spessori di scarico acqua?</p>

            {availablePlatesOptions.length === 1 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">
                        Per questo modello e categoria, l'opzione è preimpostata: <strong>{availablePlatesOptions[0].label}</strong>
                    </p>
                </div>
            )}

            <div className="space-y-3">
                {availablePlatesOptions.map((option) => (
                    <RadioOption
                        key={option.value}
                        id={`plates-${option.value}`}
                        value={option.value}
                        label={option.label}
                        selected={formData.piattiScarico}
                        onChange={(value) => onChange('piattiScarico', value)}
                        showDescription={false}
                    />
                ))}
            </div>
        </div>
    );
};

// Section 10: Price Input - Enhanced with additional fields
export const PriceSection = ({ formData, onChange }) => {
    // Calculate default validity date (6 months from today)
    const getDefaultValidityDate = () => {
        const today = new Date();
        const sixMonthsLater = new Date(today.setMonth(today.getMonth() + 6));
        const day = String(sixMonthsLater.getDate()).padStart(2, '0');
        const month = String(sixMonthsLater.getMonth() + 1).padStart(2, '0');
        const year = sixMonthsLater.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Set default validity date if not already set
    React.useEffect(() => {
        if (!formData.validitaOfferta) {
            onChange('validitaOfferta', getDefaultValidityDate());
        }
    }, [formData.validitaOfferta, onChange]);

    const MAX_TECHNICAL_IMAGES = 5;
    const [technicalImages, setTechnicalImages] = React.useState(formData.sezioneTecnicaImages || []);

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const validTypes = ['image/png', 'image/jpeg'];
        const remainingSlots = MAX_TECHNICAL_IMAGES - technicalImages.length;

        if (files.length > remainingSlots) {
            alert(`Puoi caricare al massimo ${remainingSlots} altre immagini (limite: ${MAX_TECHNICAL_IMAGES}).`);
            e.target.value = '';
            return;
        }

        for (const file of files) {
            if (!validTypes.includes(file.type)) {
                alert(`Il file "${file.name}" non è valido. Sono accettati solo JPG e PNG.`);
                e.target.value = '';
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert(`Il file "${file.name}" supera la dimensione massima di 10MB.`);
                e.target.value = '';
                return;
            }
        }

        const readPromises = files.map(file => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve({ file: reader.result, name: file.name, type: file.type });
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        }));

        Promise.all(readPromises).then(results => {
            const valid = results.filter(r => r !== null);
            const updated = [...technicalImages, ...valid];
            setTechnicalImages(updated);
            onChange('sezioneTecnicaImages', updated);
            e.target.value = '';
        });
    };

    const removeImage = (index) => {
        const updated = technicalImages.filter((_, i) => i !== index);
        setTechnicalImages(updated);
        onChange('sezioneTecnicaImages', updated);
    };

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Prezzo e Dettagli Offerta</h2>
            <p className="text-gray-600 mb-6">Inserisci i dettagli dell'offerta</p>

            <TextField
                id="prezzo"
                label="Offerta economica (€/m)"
                type="number"
                inputMode="decimal"
                placeholder="Es: 178"
                value={formData.prezzo || ''}
                onChange={(e) => onChange('prezzo', e.target.value)}
                required
                fullWidth
                helperText="Prezzo al metro lineare comprensivo di tutte le voci presenti sulla prima pagina"
            />

            <TextField
                id="quantitaStimata"
                label="Quantità Stimata (metri lineari)"
                type="number"
                inputMode="decimal"
                placeholder="Es: 50"
                value={formData.quantitaStimata || ''}
                onChange={(e) => onChange('quantitaStimata', e.target.value)}
                required
                fullWidth
            />

            <TextField
                id="validitaOfferta"
                label="Validità Offerta"
                type="text"
                placeholder="gg/mm/aaaa"
                value={formData.validitaOfferta || ''}
                onChange={(e) => onChange('validitaOfferta', e.target.value)}
                required
                fullWidth
                helperText="Data di scadenza dell'offerta (formato italiano: gg/mm/aaaa)"
            />

            <div>
                <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
                    Note
                </label>
                <textarea
                    id="note"
                    rows={6}
                    placeholder="Inserisci eventuali note o dettagli aggiuntivi..."
                    value={formData.note || ''}
                    onChange={(e) => onChange('note', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                />
            </div>

            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                    Sezione Tecnica (Opzionale)
                </label>
                <p className="text-sm text-gray-500">
                    Carica fino a {MAX_TECHNICAL_IMAGES} immagini tecniche (JPG o PNG) — ogni immagine apparirà su un foglio dedicato nel PDF
                </p>

                {technicalImages.length < MAX_TECHNICAL_IMAGES && (
                    <input
                        type="file"
                        id="sezioneTecnicaImage"
                        accept="image/png,image/jpeg"
                        multiple
                        onChange={handleFileUpload}
                        className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-lg file:border-0
                            file:text-sm file:font-semibold
                            file:bg-red-50 file:text-red-700
                            hover:file:bg-red-100
                            cursor-pointer"
                    />
                )}

                {technicalImages.length > 0 && (
                    <p className="text-sm font-medium text-gray-700">
                        {technicalImages.length} di {MAX_TECHNICAL_IMAGES} immagini caricate
                    </p>
                )}

                {technicalImages.length >= MAX_TECHNICAL_IMAGES && (
                    <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                        Hai raggiunto il limite massimo di {MAX_TECHNICAL_IMAGES} immagini. Rimuovi un'immagine per caricarne un'altra.
                    </p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {technicalImages.map((img, index) => (
                        <div key={index} className="relative p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-700 truncate max-w-[80%]">
                                    {img.name}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="text-red-600 hover:text-red-800 ml-2 flex-shrink-0"
                                    title="Rimuovi"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <img
                                src={img.file}
                                alt={`Sezione Tecnica ${index + 1}`}
                                className="w-full h-32 object-cover rounded border border-gray-300"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                    Il prezzo include:
                </p>
                <ul className="list-disc list-inside text-sm text-green-800 mt-2">
                    <li>Sistema di fissaggio in alluminio</li>
                    <li>Vetri</li>
                    <li>Installazione</li>
                    <li>Fornitura dei profili, vetri e posa in opera</li>
                </ul>
            </div>
        </div>
    );
};

// Section 11: Image Selection Section
export const ImageSelectionSection = ({ formData, onChange }) => {
    const [availableImages, setAvailableImages] = React.useState([]);
    const [loadingImages, setLoadingImages] = React.useState(true);
    const [selectedImages, setSelectedImages] = React.useState(formData.selectedEndPageImages || []);

    React.useEffect(() => {
        const loadAvailableImages = async () => {
            setLoadingImages(true);
            const modelNumber = MODEL_RENDER_FOLDER[formData.modelloNinfa] || formData.modelloNinfa.replace(/[^0-9]/g, '');

            if (!modelNumber) {
                setLoadingImages(false);
                return;
            }

            const endPageFolder = `/images/capitolato/renders/${modelNumber}/fine_pagina/`;

            // Check which images exist (1-50) by attempting to load them as Image objects
            const checkPromises = [];
            for (let imageNum = 1; imageNum <= 50; imageNum++) {
                const imagePath = `${endPageFolder}${imageNum}.jpg`;
                checkPromises.push(
                    new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => {
                            resolve({
                                index: imageNum,
                                path: imagePath,
                                selected: selectedImages.includes(imageNum)
                            });
                        };
                        img.onerror = () => {
                            resolve(null);
                        };
                        img.src = imagePath;
                    })
                );
            }

            // Wait for all checks to complete
            const results = await Promise.all(checkPromises);

            // Filter out null results (non-existent images) and sort by index
            const existingImages = results
                .filter(result => result !== null)
                .sort((a, b) => a.index - b.index);

            setAvailableImages(existingImages);
            setLoadingImages(false);

            console.log(`Found ${existingImages.length} images for model ${modelNumber}:`,
                existingImages.map(img => img.index).join(', '));
        };

        if (formData.modelloNinfa) {
            loadAvailableImages();
        }
    }, [formData.modelloNinfa]);

    const toggleImageSelection = (imageIndex) => {
        const newSelection = selectedImages.includes(imageIndex)
            ? selectedImages.filter(idx => idx !== imageIndex)
            : [...selectedImages, imageIndex].sort((a, b) => a - b);

        setSelectedImages(newSelection);
        onChange('selectedEndPageImages', newSelection);

        // Update available images display
        setAvailableImages(prev =>
            prev.map(img => ({
                ...img,
                selected: newSelection.includes(img.index)
            }))
        );
    };

    const selectAll = () => {
        const allIndices = availableImages.map(img => img.index);
        setSelectedImages(allIndices);
        onChange('selectedEndPageImages', allIndices);
        setAvailableImages(prev => prev.map(img => ({ ...img, selected: true })));
    };

    const deselectAll = () => {
        setSelectedImages([]);
        onChange('selectedEndPageImages', []);
        setAvailableImages(prev => prev.map(img => ({ ...img, selected: false })));
    };

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Selezione Immagini</h2>
            <p className="text-gray-600 mb-6">
                Seleziona le immagini che vuoi includere nel PDF dalla cartella del modello {formData.modelloNinfa}
            </p>

            {loadingImages ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
                    <span className="ml-3 text-gray-600">Caricamento immagini...</span>
                </div>
            ) : availableImages.length === 0 ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                        Nessuna immagine disponibile per questo modello nella cartella fine_pagina
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-gray-700">
                            {selectedImages.length} di {availableImages.length} immagini selezionate
                        </span>
                        <div className="space-x-2">
                            <button
                                type="button"
                                onClick={selectAll}
                                className="px-3 py-1 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium"
                            >
                                Seleziona Tutte
                            </button>
                            <button
                                type="button"
                                onClick={deselectAll}
                                className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 font-medium"
                            >
                                Deseleziona Tutte
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {availableImages.map((image) => (
                            <div
                                key={image.index}
                                onClick={() => toggleImageSelection(image.index)}
                                className={`relative cursor-pointer rounded-lg border-2 transition-all overflow-hidden ${
                                    image.selected
                                        ? 'border-red-500 ring-2 ring-red-200'
                                        : 'border-gray-200 hover:border-red-300'
                                }`}
                            >
                                <img
                                    src={image.path}
                                    alt={`Immagine ${image.index}`}
                                    className="w-full h-32 object-cover"
                                />
                                <div className="absolute top-2 right-2">
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                            image.selected
                                                ? 'bg-red-600'
                                                : 'bg-white border-2 border-gray-300'
                                        }`}
                                    >
                                        {image.selected && (
                                            <svg
                                                className="w-4 h-4 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs py-1 px-2 text-center">
                                    Immagine {image.index}
                                </div>
                            </div>
                        ))}
                    </div>

                    {selectedImages.length === 0 && (
                        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-800">
                                <strong>Attenzione:</strong> Nessuna immagine selezionata. Il PDF verrà generato senza immagini aggiuntive.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Section 12: Review Section - Enhanced with new fields
export const ReviewSection = ({ formData = {} }) => {
    const toText = (v) => {
        if (v === null || v === undefined || v === '') return '—';
        if (Array.isArray(v)) return v.join(', ');
        if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return '—'; } }
        return String(v);
    };
    const getLabel = (value, options) => {
        if (!Array.isArray(options)) return toText(value);
        const option = options.find(opt => opt.value === value);
        return option ? option.label : toText(value);
    };

    const sections = [
        {
            title: 'Informazioni Contatto', items: [
                { label: 'Nominativi', value: formData.nominativi },
                { label: 'Indirizzo', value: formData.indirizzo },
                { label: 'Telefono', value: formData.telefono },
                { label: 'Email', value: formData.email },
                { label: 'Rif. Cantiere', value: formData.rifCantiere }
            ]
        },
        {
            title: 'Configurazione Prodotto', items: [
                { label: 'Modello Ninfa', value: formData.modelloNinfa },
                { label: 'Categoria', value: getLabel(formData.categoria, CATEGORY_OPTIONS) },
                { label: 'Finitura Alluminio', value: getLabel(formData.finituraAlluminio, FINISH_OPTIONS) + (formData.ralCustomCode ? ` (${formData.ralCustomCode})` : '') },
                { label: 'Spessore Vetro', value: getLabel(formData.spessoreVetro, GLASS_OPTIONS) },
                { label: 'Tipo Fissaggio', value: getLabel(formData.tipoFissaggio, FIXING_OPTIONS) }
            ]
        },
        {
            title: 'Optional', items: [
                { label: 'Fori di Scarico', value: getLabel(formData.foriScarico, DRAINAGE_HOLES_OPTIONS) },
                { label: 'Fresatura Scarico', value: getLabel(formData.fresaturaScarico, MILLING_OPTIONS) },
                { label: 'Piatti Scarico', value: getLabel(formData.piattiScarico, PLATES_OPTIONS) }
            ]
        },
        {
            title: 'Prezzo e Dettagli Offerta', items: [
                { label: 'Prezzo al metro lineare', value: formData.prezzo ? `€ ${formData.prezzo}/m` : '—' },
                { label: 'Quantità Stimata', value: formData.quantitaStimata ? `${formData.quantitaStimata} ml` : '—' },
                { label: 'Validità Offerta', value: toText(formData.validitaOfferta) },
                { label: 'Note', value: formData.note || 'Nessuna nota' }
            ]
        }
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Riepilogo Capitolato</h2>
            <p className="text-gray-600 mb-6">Verifica i dati inseriti prima di generare il PDF</p>

            {sections.map((section, idx) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
                        {section.title}
                    </h3>
                    <dl className="space-y-3">
                        {section.items.map((item, itemIdx) => (
                            <div key={itemIdx} className={item.label === 'Note' ? 'flex flex-col' : 'flex justify-between'}>
                                <dt className="text-sm font-medium text-gray-600">{item.label}:</dt>
                                <dd className={`text-sm text-gray-900 ${item.label === 'Note' ? 'mt-2 whitespace-pre-wrap' : 'text-right max-w-md'}`}>
                                    {toText(item.value)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ))}

            {formData.sezioneTecnicaImages && formData.sezioneTecnicaImages.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
                        Sezione Tecnica
                    </h3>
                    <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">{formData.sezioneTecnicaImages.length}</span> immagini allegate (una per foglio nel PDF)
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {formData.sezioneTecnicaImages.map((img, index) => (
                                <div key={index} className="text-center">
                                    <img
                                        src={img.file}
                                        alt={`Sezione Tecnica ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                    />
                                    <p className="text-xs text-gray-500 mt-1 truncate">{img.name}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {formData.selectedEndPageImages && formData.selectedEndPageImages.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">
                        Immagini Selezionate
                    </h3>
                    <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-3">
                            <span className="font-medium">{formData.selectedEndPageImages.length}</span> immagini selezionate dalla cartella fine_pagina
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {formData.selectedEndPageImages.map((imgIndex) => (
                                <span
                                    key={imgIndex}
                                    className="px-3 py-1 bg-red-100 text-red-800 rounded-lg text-sm font-medium"
                                >
                                    Immagine {imgIndex}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};